/**
 * TrackingPlatformsPage - Plataformas de rastreamento (Gestão Rastreador › Plataformas)
 *
 * Lista paginada via `get_stock_tracking_platforms`; cadastro em TrackingPlatformFormDialog.
 * Sem exclusão: inativar preserva o histórico das homologações.
 *
 * RELEASE: 2026-10-11 v1.0.0 - Versão inicial.
 */
import { useEffect, useState } from 'react';
import { ExternalLink, Monitor, MoreHorizontal, Pencil, Plus, Power, Search } from 'lucide-react';
import { QUICK_FILTER_PILL } from '@/components/layout/list-page/quickFilterStyles';
import {
  ListPageLayout, ListPageHeader, ListPageFiltersBar, ListPageContent,
  LIST_FILTERS_INPUT_CLASS, LIST_FILTERS_SELECT_TRIGGER_CLASS,
} from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import {
  useTrackingPlatforms, useUpsertTrackingPlatform, type TrackingPlatform, type TrackingPlatformFilters,
} from '@/hooks/useTrackingPlatforms';
import { TrackingPlatformFormDialog } from '@/components/stock/TrackingPlatformFormDialog';

export default function TrackingPlatformsPage() {
  const { canCreate, canEdit, isAdmin } = usePermissionsContext();
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState<TrackingPlatformFilters>({ search: '', status: 'active', page: 1, pageSize: 25 });
  const [editor, setEditor] = useState<{ open: boolean; item: TrackingPlatform | null }>({ open: false, item: null });
  const { data, isLoading } = useTrackingPlatforms(filters);
  const upsert = useUpsertTrackingPlatform();

  // Busca com atraso para não consultar a cada tecla
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.search === searchInput.trim() ? f : { ...f, search: searchInput.trim(), page: 1 })), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const allowCreate = isAdmin || canCreate('stock_platforms');
  const allowEdit = isAdmin || canEdit('stock_platforms');
  const items = data?.items ?? [];

  const toggleActive = (p: TrackingPlatform) =>
    upsert.mutate({ id: p.id, data: { name: p.name, supplier_person_id: p.supplier_person_id, access_url: p.access_url, is_active: !p.is_active } });

  return (
    <ListPageLayout>
      <ListPageHeader title="Plataformas de rastreamento"
        actions={allowCreate ? (
          <Button size="xs" onClick={() => setEditor({ open: true, item: null })}><Plus className="mr-1.5 h-4 w-4" /> Nova plataforma</Button>
        ) : undefined} />
      <ListPageFiltersBar>
        <div className="relative min-w-0 flex-1 sm:max-w-sm">
          <Search className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground sm:h-3.5 sm:w-3.5" />
          <Input className={`${LIST_FILTERS_INPUT_CLASS} w-full pl-8`} value={searchInput} maxLength={100}
            placeholder="Buscar por nome ou fornecedor..." onChange={(e) => setSearchInput(e.target.value)} />
        </div>
        <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v as TrackingPlatformFilters['status'], page: 1 }))}>
          <SelectTrigger className={`${LIST_FILTERS_SELECT_TRIGGER_CLASS} ${QUICK_FILTER_PILL} shrink-0`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Ativas</SelectItem>
            <SelectItem value="inactive">Inativas</SelectItem>
            <SelectItem value="all">Todas</SelectItem>
          </SelectContent>
        </Select>
      </ListPageFiltersBar>
      <ListPageContent>
        {isLoading ? <Skeleton className="h-40 w-full rounded-lg" /> : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <Monitor className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Nenhuma plataforma encontrada.</p>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <Table className="min-w-[700px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Fornecedor</TableHead>
                  <TableHead>URL de Acesso</TableHead>
                  <TableHead className="w-24">Situação</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((p) => (
                  <TableRow key={p.id} className="cursor-pointer" onClick={() => allowEdit && setEditor({ open: true, item: p })}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-sm">{p.supplier_name ?? '—'}</TableCell>
                    <TableCell className="text-sm" onClick={(e) => e.stopPropagation()}>
                      {p.access_url ? (
                        <a href={p.access_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                          <span className="max-w-[260px] truncate">{p.access_url}</span><ExternalLink className="h-3 w-3 shrink-0" />
                        </a>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider">
                        <StatusDot tone={p.is_active ? 'emerald' : 'muted'} />{p.is_active ? 'Ativa' : 'Inativa'}
                      </Badge>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {allowEdit && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Ações"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditor({ open: true, item: p })}><Pencil className="mr-2 h-4 w-4" /> Editar</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggleActive(p)}><Power className="mr-2 h-4 w-4" /> {p.is_active ? 'Inativar' : 'Ativar'}</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <PaginationFooter totalItems={data?.total ?? 0} currentPage={filters.page} pageSize={filters.pageSize}
          onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
          onPageSizeChange={(pageSize) => setFilters((f) => ({ ...f, pageSize, page: 1 }))}
          pageSizeOptions={[10, 25, 50, 100]} />
      </ListPageContent>
      <TrackingPlatformFormDialog open={editor.open} platform={editor.item} onOpenChange={(open) => setEditor((c) => ({ ...c, open }))} />
    </ListPageLayout>
  );
}
